import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import type { Observable } from 'rxjs';
import { combineLatest, combineLatestWith, map, of, switchMap, take } from 'rxjs';
import {
    CreateGraphReportDocument,
    GetMeasuringPointsDocument,
    GraphReportsDocument,
    Permission,
    ReportTemplateEntityType,
    UpdateGraphReportLastDownloadedDocument,
    type CreateGraphReportMutation,
    type CreateGraphReportMutationVariables,
    FileType,
    type FrequencyGraphScaleType,
    type GetMeasuringPointsQuery,
    type GetMeasuringPointsQueryVariables,
    type GraphReportsQuery,
    type GraphReportsQueryVariables,
    type GraphReportsTypeFieldsFragment,
    type GraphReportTypeFieldsFragment,
    type GraphType,
    type UpdateGraphReportLastDownloadedMutation,
    type UpdateGraphReportLastDownloadedMutationVariables,
    ReportStatus,
    type SwarmType,
} from '@/graphql/types';
import { hasAtLeastPermission, requirePermission } from '../../pages/sharing/utils';
import type { DataSet, MultiSensorDataSet } from '../../../graph/dataset';
import type { Scales } from '../../../graph/graph-helpers';
import { overrides$ } from '../../../graph/graph-helpers';
import { revertDistanceConverter } from '../../../utils/formatting';
import { useProfile } from '../../../profile';
import { createFallbackName } from '../../../utils/string';
import warning from '../../../utils/logger';
import { addNotificationHack, NOTIFICATION_VARIANT } from '../../notifications/Notifications';
import { updateReadyReportsCount } from '../../utils';
import type { LegendData } from '../../../graph/legend-helpers';
import { DATA_TYPE } from '../../../graph/enums';

export const FILE_TYPES_DISPLAY_TEXT = Object.freeze({
    [FileType.Pdf]: 'PDF',
    [FileType.Csv]: 'CSV',
    [FileType.Xlsx]: 'Excel (xlsx)',
});

function shouldStopPollingReports(reportIds: string[], reportsData?: GraphReportsQuery) {
    if (!reportIds.length) {
        return true;
    }

    const edges = reportsData?.graphReports?.edges as Array<{
        node: GraphReportTypeFieldsFragment;
    }>;
    if (!edges?.length) {
        return false;
    }

    const possibleInProgressReports = new Set(reportIds);

    edges.forEach((edge) => {
        if (edge.node.status !== ReportStatus.InProgress) {
            possibleInProgressReports.delete(edge.node.id);
        }
    });

    return possibleInProgressReports.size === 0;
}

export function useHandleFileTypeExports(
    dataSet: DataSet & Pick<MultiSensorDataSet, 'currentSensor$'>,
    graphType$: Observable<GraphType>,
    scales: Scales,
    legendData: LegendData,
    frequencyGraphScaleType$: Observable<FrequencyGraphScaleType>
) {
    const { distanceUnit } = useProfile();
    const [reportIds, setReportIds] = useState<string[]>([]);
    const [showStatusPopup, setShowStatusPopup] = useState(false);
    const [downloadFiles, setDownloadFiles] = useState<GraphReportTypeFieldsFragment[]>([]);

    const [fetchGraphReports, { data: reportsData, stopPolling, refetch }] = useLazyQuery<
        GraphReportsQuery,
        GraphReportsQueryVariables
    >(GraphReportsDocument, {
        variables: {
            ids: reportIds,
        },
        pollInterval: 5000,
    });

    const [createGraphReportMutation] = useMutation<
        CreateGraphReportMutation,
        CreateGraphReportMutationVariables
    >(CreateGraphReportDocument, {
        onCompleted(result) {
            if (result?.createGraphReport) {
                setShowStatusPopup(true);
                setReportIds((ids) => [
                    ...ids,
                    (result.createGraphReport?.graphReport as GraphReportsTypeFieldsFragment)?.id,
                ]);
                void fetchGraphReports();
            }
        },
        onError() {
            addNotificationHack({
                message: gettext('DATA_EXPORT_UNAVAILABLE_ERROR'),
                variant: NOTIFICATION_VARIANT.ERROR,
            });
        },
    });

    const [updateLastDownloadMutation] = useMutation<
        UpdateGraphReportLastDownloadedMutation,
        UpdateGraphReportLastDownloadedMutationVariables
    >(UpdateGraphReportLastDownloadedDocument, {
        onCompleted() {
            void refetch();
        },
    });

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const datePickerExportType = useRef<string | undefined>();

    const openDatePickerForExportType = useCallback((exportType: string) => {
        datePickerExportType.current = exportType;
        setIsDatePickerOpen(true);
    }, []);

    const createGraphReport = useCallback(
        (
            exportType: FileType,
            {
                projectId,
                ...extraParameters
            }: { projectId?: string } & Record<string, unknown> = {},
            customDateRange: [number, number] | null = null
        ) => {
            const frequencyScale$ = scales.createObservableForType(DATA_TYPE.FDOM);
            const valueScale$ = dataSet.dataType$.pipe(
                switchMap((dataType) => scales.createObservableForType(dataType)),
                map((v) => revertDistanceConverter(v, distanceUnit))
            );

            combineLatest([
                customDateRange ? of(customDateRange) : dataSet.dateRange.observable$,
                graphType$,
                dataSet.dataType$,
                dataSet.currentSensor$,
                overrides$.pipe(
                    combineLatestWith(legendData.hiddenTypes$),
                    map(([overrides, hiddenTypes]) =>
                        JSON.stringify({
                            ...overrides,
                            // Add the hidden lines to the override values.
                            hidden_lines: hiddenTypes,
                        })
                    )
                ),
                frequencyScale$,
                valueScale$,
                frequencyGraphScaleType$,
            ])
                .pipe(take(1))
                .subscribe(
                    ([
                        [min, max],
                        graphType,
                        dataType,
                        currentSensor,
                        overrides,
                        frequencyScale,
                        valueScale,
                        frequencyGraphScaleType,
                    ]) => {
                        const parameters = {
                            viewStart: min,
                            viewEnd: max,
                            dataType,
                            graphType,
                            accelerationScale: valueScale,
                            frequencyScale,
                            frequencyGraphScaleType,
                            ...extraParameters,
                            overrides: JSON.stringify(overrides),
                        };

                        if (!currentSensor) {
                            throw new Error(
                                'No current sensor selected for multi-sensor data set.'
                            );
                        }

                        const id = projectId || currentSensor;
                        const entityType = projectId
                            ? ReportTemplateEntityType.Project
                            : ReportTemplateEntityType.MeasuringPoint;

                        void createGraphReportMutation({
                            variables: {
                                id,
                                entityType,
                                fileType: exportType,
                                parameters,
                                sendEmailNotification: true,
                            },
                        });
                    }
                );
        },
        [
            createGraphReportMutation,
            dataSet.currentSensor$,
            dataSet.dataType$,
            dataSet.dateRange.observable$,
            graphType$,
            scales,
            legendData.hiddenTypes$,
            frequencyGraphScaleType$,
            distanceUnit,
        ]
    );

    useEffect(() => {
        if (reportsData) {
            updateReadyReportsCount(reportsData.graphReports?.readyReportsCount ?? 0);
        }
        setDownloadFiles(
            reportsData?.graphReports && reportIds.length
                ? reportsData.graphReports.edges.map(
                      (edge) => edge!.node as GraphReportTypeFieldsFragment
                  )
                : []
        );
    }, [reportsData, reportIds]);

    useEffect(() => {
        if (shouldStopPollingReports(reportIds, reportsData)) {
            stopPolling();
        }
    }, [reportsData, reportIds, stopPolling]);

    useEffect(() => {
        // Reset reportIds when status popup gets closed.
        if (!showStatusPopup && reportIds.length) {
            setReportIds([]);
        }
    }, [reportIds, showStatusPopup]);

    return {
        dataSet,
        createGraphReport,
        datePickerExportType,
        openDatePickerForExportType,
        isDatePickerOpen,
        setIsDatePickerOpen,
        setShowStatusPopup,
        showStatusPopup,
        downloadFiles,
        updateLastDownloadMutation,
    };
}

type QueryMeasuringPoint = NonNullable<
    NonNullable<GetMeasuringPointsQuery['measuringPoints']>[number]
>;
type QueryProject = NonNullable<NonNullable<GetMeasuringPointsQuery['projects']>[number]>;
type ExportSelectorMeasuringPoint = Omit<QueryMeasuringPoint, 'project'> & {
    project:
        | ({ canExportProjectTemplate: true } & QueryProject)
        | { canExportProjectTemplate: false; name: string }
        | null;
};

export function mapMeasuringPointsForExportSelector(
    measuringPoints: QueryMeasuringPoint[],
    projects: Array<QueryProject | null | undefined>
) {
    const projectsById = new Map(
        projects
            .filter((project): project is QueryProject => project != null)
            .map((project) => [project.id, project])
    );

    return measuringPoints.map<ExportSelectorMeasuringPoint>((measuringPoint) => {
        if (!measuringPoint.project) {
            return {
                ...measuringPoint,
                project: null,
            };
        }

        const project = projectsById.get(measuringPoint.project);
        if (project) {
            // Exporting a project template needs view access.
            // A reference-only project is shown by name only.
            const permission = requirePermission(project.permission, `project "${project.id}"`);
            if (!hasAtLeastPermission(permission, Permission.View)) {
                return {
                    ...measuringPoint,
                    project: {
                        canExportProjectTemplate: false,
                        name: project.name,
                    },
                };
            }

            return {
                ...measuringPoint,
                project: {
                    canExportProjectTemplate: true,
                    id: project.id,
                    name: project.name,
                },
            };
        }

        // The project of a visible measuring point is always visible, at least by reference.
        // Getting here means the permission cache is out of step, so report it and keep the
        // page usable. Remove this branch if it never shows up in Rollbar.
        warning('Project of a visible measuring point is missing from the projects list.', {
            measuringPointId: measuringPoint.id,
            projectId: measuringPoint.project,
        });

        return {
            ...measuringPoint,
            project: {
                canExportProjectTemplate: false,
                name: createFallbackName(measuringPoint.project),
            },
        };
    });
}

interface UseMeasuringPointsQueryOptions {
    swarmType?: SwarmType;
    skip?: boolean;
}

export function useMeasuringPointsQuery({
    swarmType,
    skip = false,
}: UseMeasuringPointsQueryOptions = {}) {
    const { data, loading, error } = useQuery<
        GetMeasuringPointsQuery,
        GetMeasuringPointsQueryVariables
    >(GetMeasuringPointsDocument, {
        variables: { swarmType },
        skip,
        fetchPolicy: 'cache-and-network',
    });

    const measuringPoints = useMemo(() => {
        if (!data || !data.measuringPoints) return [];

        const nodes = data.measuringPoints.filter(
            (measuringPoint): measuringPoint is QueryMeasuringPoint => measuringPoint != null
        );

        return mapMeasuringPointsForExportSelector(nodes, data.projects ?? []);
    }, [data]);

    return { measuringPoints, loading, error };
}
